import { IClause } from "../../interfaces";
import { SuggestionResponse } from "../../models/responses/suggestion";
import { QueryBuilderContext } from "../../types/enums";
import { textToSanitizedKeywordsText } from "../../utils/helpers";
import SearchQueryBuilder from "../../utils/searchQueryBuilder";
import { transformDocumentsResponse } from "../document/helper";
import { SearchSuggestion, SimilarSuggestionSearch } from "./types";

const generateRankingKeywords = (text: string): string[] => {
  const keywordSet = new Set<string>();
  const quoteReg = new RegExp(/"(.*?)"/, "gi");
  const quotedTexts = Array.from(text.matchAll(quoteReg)).map((value) => value[1]);
  const cleanKeywords = text
    .replace(quoteReg, " ")
    .replace(/[^A-Za-z0-9 $]/gi, " ")
    .replace(/(AND|OR)/g, " ")
    .replace(/ +/g, " ")
    .trim()
    .split(" ")
    .concat(quotedTexts)
    .filter((w) => !!w.trim());

  cleanKeywords.forEach((w: string) => keywordSet.add(w.trim().toLowerCase()));
  return Array.from(keywordSet);
};

const genBoolRankingKeywords = (tsQuery: string): string[] => {
  const keywordSet = new Set<string>();
  // Remove negated words and group and wildcard words
  const negatedWordRegex = /!\w+/gi;
  const negatedBlockRegex = /!\((?:[^()]+|\([^()]+\){0,5}){0,5}\)/gi;
  const joinRegex = /(<->)|(\|)/gi;

  const words = tsQuery
    .replace(negatedWordRegex, "")
    .replace(negatedBlockRegex, "")
    .replace(/\w+\:\*/gi, "")
    .replace(joinRegex, "&")
    .replace(/(\()|(\))/gi, "")
    .trim()
    .split("&")
    .filter((w) => !!w.trim());

  words.forEach((w: string) => keywordSet.add(w.trim().toLowerCase()));
  return Array.from(keywordSet);
};

export const suggestionRequestBuilder = (values: SearchSuggestion, addToActivities: boolean) => {
  const queryBuilder = new SearchQueryBuilder(QueryBuilderContext.CLAUSE_SEARCH);
  const conditions = queryBuilder.buildQuery(values);

  const rankingKeywords = !values.isBooleanSearch
    ? generateRankingKeywords(textToSanitizedKeywordsText(values.searchText))
    : genBoolRankingKeywords(queryBuilder.getQueryField<string>(conditions, ["rule.field", "keywords"], "rule.value"));

  return {
    filter: {
      addToActivities,
      disableExternalTextSearch: values.isBooleanSearch,
      returnDocumentCount: true,
      limit: 100,
      conditions,
      rankingOptions: {
        keywords: rankingKeywords,
        filterKeywords: values.keywordsFilter ?? "",
      },
    },
  };
};

export const similarSuggestionRequestBuilder = (values: SimilarSuggestionSearch) => {
  const queryBuilder = new SearchQueryBuilder(QueryBuilderContext.CLAUSE_SEARCH);
  const conditions = queryBuilder.buildQuery({
    searchText: values.searchText,
    excludeClauses: values.excludeIds,
  });

  const rankingKeywords = generateRankingKeywords(textToSanitizedKeywordsText(values.searchText));

  return {
    filter: {
      addToActivities: true,
      disableExternalTextSearch: false,
      limit: 50,
      conditions,
      rankingOptions: {
        keywords: rankingKeywords,
      },
    },
  };
};

const mapResponseSuggestions = <T>(suggestions: T[] | T): IClause[] | IClause => {
  const mapToFE = (sug: any) => {
    const mergedTagIds = Array.from(new Set([...sug.document.tagIds, ...sug.tagIds]));
    return {
      clause_id: sug.clauseId,
      textBlockId: sug.textBlockId,
      clause_text: sug.text,
      is_endorsed: !!sug.endorsed,
      title: sug.title,
      similarity: sug.similarity,
      rankedSimilarity: sug.similarity,
      id: +sug.clauseId,
      date: sug.date,
      created: sug.created,
      is_favorite: !!sug.userFlags?.favorite,
      is_bookmarked: !!sug.userFlags?.bookmark,
      like_status: sug.userFlags?.likeStatus !== "NEUTRAL",
      sourceDocuments: transformDocumentsResponse(sug.sourceDocuments),
      tagIds: sug.tagIds,
      mergedTagIds,
    } as IClause;
  };
  if (suggestions instanceof Array) {
    if (!suggestions.length) {
      return [] as Array<IClause>;
    }
    return suggestions.map(mapToFE);
  } else {
    return mapToFE(suggestions);
  }
};

const suggestionTransformer = <T, V>(suggestions: T): V => {
  return mapResponseSuggestions(suggestions) as V;
};

export const transformSuggestionsResponse = (suggestions: SuggestionResponse[]) => {
  return suggestionTransformer<SuggestionResponse[], IClause[]>(suggestions);
};

export const transformClauses = ({ clauses = [] }) => transformSuggestionsResponse(clauses);
